import React from 'react';
import { CodeDiffLine } from './CodeDiffLine';
import styles from './CodeDiffViewer.module.scss';
import cx from 'classnames';

interface CodeDiffViewerProps {
  oldCode: string;
  newCode: string;
  className?: string;
}

/**
 * CodeDiffViewer component that displays differences between two code strings
 * using a unified diff format with line numbers and change indicators.
 */
export const CodeDiffViewer: React.FC<CodeDiffViewerProps> = ({ oldCode, newCode, className }) => {
  const diffLines = generateUnifiedDiff(oldCode, newCode);

  return (
    <div className={cx(styles.diffContainer, className)}>
      {diffLines.map((diffLine, index) => (
        <CodeDiffLine key={`diff-line-${index}`} line={diffLine} />
      ))}
    </div>
  );
};

/**
 * Diff line object representing a single line in the unified diff output
 */
interface DiffLine {
  type: 'unchanged' | 'added' | 'removed';
  content: string;
  oldLineNumber: number | null;
  newLineNumber: number | null;
}

/**
 * Generates a unified diff between two code strings.
 *
 * This function implements a simple line-by-line comparison algorithm that:
 * 1. Compares lines at the same position in both files
 * 2. Marks matching lines as 'unchanged'
 * 3. Marks new lines as 'added' (present in newCode but not in oldCode)
 * 4. Marks deleted lines as 'removed' (present in oldCode but not in newCode)
 *
 * Note: This is a simplified diff algorithm. For more complex scenarios,
 * consider using a library like 'diff' or implementing the Myers diff algorithm.
 *
 * @param oldCode - The original code string
 * @param newCode - The modified code string
 * @returns Array of DiffLine objects representing the unified diff
 */
function generateUnifiedDiff(oldCode: string, newCode: string): DiffLine[] {
  const oldCodeLines = oldCode.split('\n');
  const newCodeLines = newCode.split('\n');
  const diffResult: DiffLine[] = [];

  let oldLineIndex = 0;
  let newLineIndex = 0;

  // Iterate through both arrays simultaneously, comparing lines at the same position
  while (oldLineIndex < oldCodeLines.length || newLineIndex < newCodeLines.length) {
    const currentOldLine = oldCodeLines[oldLineIndex];
    const currentNewLine = newCodeLines[newLineIndex];

    // Case 1: Both lines exist and are identical (unchanged line)
    if (
      oldLineIndex < oldCodeLines.length &&
      newLineIndex < newCodeLines.length &&
      currentOldLine === currentNewLine
    ) {
      diffResult.push({
        type: 'unchanged',
        content: currentOldLine,
        oldLineNumber: oldLineIndex + 1,
        newLineNumber: newLineIndex + 1,
      });
      oldLineIndex++;
      newLineIndex++;
    }
    // Case 2: New line exists but old line doesn't match (added line)
    else if (
      newLineIndex < newCodeLines.length &&
      (oldLineIndex >= oldCodeLines.length || currentOldLine !== currentNewLine)
    ) {
      diffResult.push({
        type: 'added',
        content: currentNewLine,
        oldLineNumber: null,
        newLineNumber: newLineIndex + 1,
      });
      newLineIndex++;
    }
    // Case 3: Old line exists but new line doesn't match (removed line)
    else if (
      oldLineIndex < oldCodeLines.length &&
      (newLineIndex >= newCodeLines.length || currentOldLine !== currentNewLine)
    ) {
      diffResult.push({
        type: 'removed',
        content: currentOldLine,
        oldLineNumber: oldLineIndex + 1,
        newLineNumber: null,
      });
      oldLineIndex++;
    }
  }

  return diffResult;
}
